"use client";

import { faArrowsRotate, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { Icon } from "@/components/ui/Icon";
import { reloadForUpdate } from "@/lib/app-update";
import { useStaleDeploymentError } from "@/lib/app-update-boundary";
import { useReportBoundaryError } from "@/lib/observability/report-client";

// Root error boundary for everything outside the manage panel (storefront,
// account, checkout). Deliberately provider-free (no next-intl hooks) so it
// can never fail itself — copy is hardcoded bilingual.
export default function RootError({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	// Was this a redeploy rather than a defect? Next 16 answers precisely — a
	// server action the new build no longer has answers 404 with
	// `x-nextjs-action-not-found`, which the router turns into an
	// UnrecognizedActionError. Saying "something went wrong" for that is the
	// misdiagnosis this branch exists to stop: nothing went wrong with their
	// input, the tab is behind. See lib/app-update-boundary.ts.
	//
	// ⚠️ Computed BEFORE the report, because it decides whether there is anything
	// to report: a redeploy the screen below already explains is not a defect.
	const staleDeploy = useStaleDeploymentError(error);

	// TRY-418: the digest below is already on the customer's screen; this is what
	// puts a matching record on the server. Never throws — see the hook's header.
	useReportBoundaryError("root", error, { explained: staleDeploy });

	return (
		<div className="mx-auto flex max-w-lg flex-col items-center gap-4 px-6 py-24 text-center">
			<span className="text-4xl" aria-hidden>
				<Icon icon={faTriangleExclamation} size={14} />
			</span>
			<h1 className="text-xl font-bold">
				{staleDeploy ? "حدثت تحديثات على جنى، أعد تحميل الصفحة" : "حدث خطأ غير متوقع"}
			</h1>
			<p className="text-sm text-muted-foreground">
				{staleDeploy ? (
					"Jana was updated while this page was open, so your last action did not go through. Reload and try again. Nothing was saved."
				) : (
					<>Something went wrong. Please try again. If it keeps happening, contact the store.</>
				)}
			</p>
			{error.digest && (
				<p className="font-mono text-xs text-muted-foreground">
					ref:
					{error.digest}
				</p>
			)}
			{/* ⚠️ reset() is the WRONG affordance for a stale deployment and would
			    read as a working retry: it re-renders the same old bundle, whose
			    action ids are still the ones the server does not have, so every
			    press fails identically. Only a full load fetches the new bundle.
			    And it is still a button rather than an automatic reload — a
			    redeploy interrupts people mid-form, and `<form action>` state does
			    not survive a page load. */}
			{staleDeploy ? (
				<button
					onClick={reloadForUpdate}
					className="btn btn-primary flex items-center gap-2 text-sm"
				>
					<Icon icon={faArrowsRotate} size={14} />
					إعادة التحميل · Reload
				</button>
			) : (
				<button onClick={() => reset()} className="btn btn-primary text-sm">
					إعادة المحاولة · Try again
				</button>
			)}
		</div>
	);
}
